"use client";

import React from "react";
import Image from "next/image";
import useTheme from "src/context/appThemeContext";
import { CalendarDays, PartyPopper, Coffee } from "lucide-react";

const TychoLifeHero = () => {
  const { theme, mounted } = useTheme();

  const imgSrc = mounted && theme === "dark" ? "/images/serviceBanners/lifeT.webp" : "/images/serviceBanners/lifeTB.webp";

  return (
    <section className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px]">
        <Image src={imgSrc} alt="Life at Tycho" fill priority className="object-cover object-right" />
      </div>
      <div className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src={imgSrc} alt="Life at Tycho" fill priority className="object-contain object-right" />
      </div>

      <div
        className="absolute inset-0 z-10 hidden lg:block"
        style={{
          background:
            mounted && theme === "dark"
              ? "linear-gradient(90deg, #080a17 0%, #080a17 50%, rgba(8, 10, 23, 1) 15%, rgba(8, 10, 23,1) 20%, transparent 85%)"
              : "linear-gradient(90deg, rgba(240, 242, 255, 0.95) 0%, rgba(240, 242, 255, 1) 45%, rgba(240, 242, 255, 0.9) 50%, rgba(240, 242, 255, 0.25) 65%, transparent 100%)",
        }}
      />

      <div className="relative z-20 flex flex-col justify-center  px-6! !py-10">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4!">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
              INSIDE THE CULTURE
            </h3>

            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">Life @</span>{" "}
            <span className="text-text-primary">Tycho.</span>
          </h1>

          <p className="text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            From day one, we have built a culture where innovation, collaboration, and creativity grow together. At Tycho, success is not only defined by projects and performance
            but also by meaningful relationships, shared learning, and continuous support. We believe in celebrating every milestone as a team through engaging experiences,
            creative freedom, and positive energy. Our workplace encourages open communication, teamwork, and personal growth while fostering a vibrant and inspiring environment.
            Life at Tycho is about building strong connections, enjoying the journey, and growing together while contributing to impactful digital solutions.
          </p>

          <div className="flex flex-wrap items-center gap-x-4! sm:gap-x-12! md:gap-x-16! gap-y-6!">
            <div className="flex items-center gap-3!">
              <div className="w-9 h-9 rounded-full bg-[#6366f1] flex items-center justify-center shrink-0">
                <CalendarDays className="w-4.5 h-4.5 text-white" />
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-text-primary">10+</h3>
                <p className="text-text-secondary text-xs sm:text-sm">Years together</p>
              </div>
            </div>

            <div className="flex items-center gap-3!">
              <div className="w-9 h-9 rounded-full bg-[#a855f7] flex items-center justify-center shrink-0">
                <PartyPopper className="w-4.5 h-4.5 text-white" />
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-text-primary">40+</h3>
                <p className="text-text-secondary text-xs sm:text-sm">Celebrations / yr</p>
              </div>
            </div>

            <div className="flex items-center gap-3!">
              <div className="w-9 h-9 rounded-full bg-[#3b82f6] flex items-center justify-center shrink-0">
                <Coffee className="w-4.5 h-4.5 text-white" />
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-text-primary">100%</h3>
                <p className="text-text-secondary text-xs sm:text-sm">Caffeinated</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default TychoLifeHero;
